/**
 * Inline citation chip. Builds a readable label out of the
 * structured LegalReference fields (instrument + article/annex/...).
 * Mono font for the citation body per UI-SYSTEM.md §1.5 red line.
 */

import type { ComplianceRequirementCard } from "@regpulse/contracts";

type LegalReference = ComplianceRequirementCard["legalReferences"][number];

const GRANULARITY_PREFIX: Record<LegalReference["granularity"], string> = {
  regulation: "",
  chapter: "ch.",
  section: "§",
  article: "art.",
  annex: "annex",
  paragraph: "¶",
  subparagraph: "subpara.",
};

function buildCitation(ref: LegalReference): string {
  const tail = ref.article ?? ref.annex ?? ref.section ?? ref.paragraph;
  const prefix = GRANULARITY_PREFIX[ref.granularity];
  if (!tail) return ref.instrument;
  return prefix
    ? `${ref.instrument}, ${prefix} ${tail}`
    : `${ref.instrument} ${tail}`;
}

export function LegalReferenceBadge({ reference }: { reference: LegalReference }) {
  const text = buildCitation(reference);
  const isGap = Boolean(reference.citationGapReason);
  return (
    <span
      title={reference.uri ?? reference.citationGapReason ?? undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--space-4)",
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        border: isGap
          ? "1px dashed var(--status-unknown-config)"
          : "1px solid var(--color-border-strong)",
        borderRadius: "var(--radius-4)",
        padding: "1px var(--space-8)",
        background: isGap
          ? "var(--status-unknown-config-soft)"
          : "var(--color-surface-sunken)",
        color: "var(--color-text-primary)",
      }}
    >
      <span style={{ color: "var(--color-text-secondary)" }}>{reference.granularity}</span>
      <span>{text}</span>
      {isGap && (
        <span aria-label="citation gap" style={{ color: "var(--status-unknown-config)" }}>⚠</span>
      )}
    </span>
  );
}
